import { SHStack, SHTableCell, SHTableRow } from "@components/design-systems";
import {
  AnalysisFeePortfolioDTO,
  FeeCalculatorDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { useFormContext } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { defaultTo } from "lodash";
import { DeleteButton } from "@components/buttons/delete";
import { useTheme } from "@mui/material";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import {
  calculateInvestmentValueByWeight,
  calculateInvestmentWeightByValue,
  getFeeCalculatorRowFieldPath,
} from "../../utils";
import { RowLabel } from "../investment-label";
import { NumericInputController } from "../numeric-input-controller";

type Props = {
  item: FeeCalculatorDTO;
  itemIndex: number;
  calculatedTotal: number;
  disabled?: boolean;

  onDelete: () => void;
};

export const FeeCalculatorRow = ({
  item,
  itemIndex,
  calculatedTotal,
  disabled = false,
  onDelete,
}: Props) => {
  const { palette } = useTheme();
  const formMethods = useFormContext<AnalysisFeePortfolioDTO>();
  const fieldPaths = getFeeCalculatorRowFieldPath({ itemIndex });

  const investmentValue = formMethods.watch(fieldPaths.value);
  const investmentWeight = formMethods.watch(fieldPaths.weight);

  const handleOnDelete = () => {
    handleOnChangeInvestmentWeight(0);
    onDelete();
  };

  const handleOnChangeInvestmentWeight = (value: number) => {
    const investmentValue = calculateInvestmentValueByWeight({
      total: calculatedTotal,
      weight: value,
    });
    formMethods.setValue(fieldPaths.value, investmentValue.toString());
  };

  const handleOnChangeInvestmentValue = (value: number) => {
    const investmentWeight = calculateInvestmentWeightByValue({
      total: calculatedTotal,
      value,
    });
    formMethods.setValue(fieldPaths.weight, investmentWeight);
  };

  useEffect(() => {
    const weightNumberValue = defaultTo(Number(investmentWeight), 0);
    if (weightNumberValue !== 0 && Number(investmentValue) === 0) {
      handleOnChangeInvestmentWeight(weightNumberValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentWeight, investmentValue]);

  const shouldDisableRow = useMemo(() => {
    return (
      item.isMissingInvestment ||
      item.hasUnavailableInvestmentOption ||
      disabled
    );
  }, [item, disabled]);

  return (
    <SHTableRow
      className={"AnalysisFeePortfolioSetup-table-row"}
      sx={{ width: "100%" }}
    >
      <SHTableCell>
        <SHStack
          direction={"row"}
          alignContent={"center"}
          alignItems={"center"}
        >
          <SHStack
            width={80}
            position={"relative"}
            flexShrink={0}
            alignItems={"start"}
            justifyContent={"start"}
          >
            {shouldDisableRow && !disabled && (
              <DeleteButton
                height={24}
                width={24}
                backgroundColor={"transparent"}
                borderColor={palette.common.black}
                iconColor={palette.common.black}
                tabIndex={-1}
                onClick={handleOnDelete}
              />
            )}
          </SHStack>

          <RowLabel
            label={item.secondaryName ?? item.name ?? ""}
            disabled={shouldDisableRow}
            isMissingInvestmentOption={item.isMissingInvestment}
            balanceAvailability={item.balanceAvailability}
            isUnavailable={item.hasUnavailableInvestmentOption}
            isIDPSBalance={
              item.variableName === AnalysisVariable.IDPS_Cash_balance
            }
            isSuperBalance={
              item.variableName === AnalysisVariable.Super_Cash_balance
            }
          />
        </SHStack>
      </SHTableCell>

      <SHTableCell width={120}>
        <NumericInputController
          type={"weight"}
          disabled={shouldDisableRow}
          formMethods={formMethods}
          fieldPath={fieldPaths.weight}
          onValueChange={handleOnChangeInvestmentWeight}
        />
      </SHTableCell>

      <SHTableCell width={220}>
        <NumericInputController
          type={"value"}
          disabled={shouldDisableRow}
          formMethods={formMethods}
          fieldPath={fieldPaths.value}
          onValueChange={handleOnChangeInvestmentValue}
        />
      </SHTableCell>
    </SHTableRow>
  );
};
